// palettes.js — 8 phase-specific palettes for solar-synced theming.
//
// Each palette defines the same set of CSS variable names (bg, bgCard,
// bgInset, text, textSecondary, textTertiary, border, accent, accentSoft,
// accentBorder, success, warning, danger, their -Soft variants, shadow).
// The provider swaps the entire palette on phase change, so components that
// read `var(--color-bg)` etc. re-paint automatically.
//
// Design intent:
//   pre-dawn  — deep indigo, violet hints, reduced contrast
//   dawn      — warm peach/amber rising, softly lit
//   morning   — clean cream + teal (focused work feel)
//   noon      — crisp near-white with blue accent (max clarity)
//   afternoon — neutral soft-grey + teal (energy dip, easy on eyes)
//   golden    — amber + rose warm (winding down)
//   dusk      — lavender + burgundy (transition)
//   night     — dark + purple (V2 NIGHT parity, low-strain)
//
// Accents intentionally rotate with the day so the app *feels* different at
// 7am vs 7pm even though function is identical.

export const PALETTES = {
  'pre-dawn': {
    bg:            '#0B0B18',
    bgCard:        '#141425',
    bgInset:       '#1C1C32',
    text:          '#E7E3F5',
    textSecondary: '#A8A5BE',
    textTertiary:  '#6A6782',
    border:        'rgba(232,228,245,0.08)',
    accent:        '#7C6CFF',
    accentSoft:    'rgba(124,108,255,0.14)',
    accentBorder:  'rgba(124,108,255,0.3)',
    success:       '#34d399',
    successSoft:   'rgba(52,211,153,0.12)',
    warning:       '#fbbf24',
    warningSoft:   'rgba(251,191,36,0.12)',
    danger:        '#f87171',
    dangerSoft:    'rgba(248,113,113,0.12)',
    shadow:        '0 6px 18px rgba(0,0,0,0.5)',
    scheme:        'dark',
  },
  'dawn': {
    bg:            '#FFF2E1',
    bgCard:        '#FFFAF0',
    bgInset:       '#FCE9CF',
    text:          '#3A2615',
    textSecondary: '#6E4B2A',
    textTertiary:  '#9B7043',
    border:        'rgba(58,38,21,0.12)',
    accent:        '#E88A5A',
    accentSoft:    'rgba(232,138,90,0.14)',
    accentBorder:  'rgba(232,138,90,0.32)',
    success:       '#16a34a',
    successSoft:   'rgba(22,163,74,0.12)',
    warning:       '#d97706',
    warningSoft:   'rgba(217,119,6,0.12)',
    danger:        '#dc2626',
    dangerSoft:    'rgba(220,38,38,0.1)',
    shadow:        '0 6px 18px rgba(132,72,28,0.12)',
    scheme:        'light',
  },
  'morning': {
    bg:            '#FBFAF6',
    bgCard:        '#FFFFFF',
    bgInset:       '#F1EFE7',
    text:          '#1A1F26',
    textSecondary: '#4F5862',
    textTertiary:  '#8A93A0',
    border:        'rgba(26,31,38,0.1)',
    accent:        '#0D9488',
    accentSoft:    'rgba(13,148,136,0.1)',
    accentBorder:  'rgba(13,148,136,0.3)',
    success:       '#059669',
    successSoft:   'rgba(5,150,105,0.1)',
    warning:       '#d97706',
    warningSoft:   'rgba(217,119,6,0.1)',
    danger:        '#dc2626',
    dangerSoft:    'rgba(220,38,38,0.08)',
    shadow:        '0 4px 14px rgba(26,31,38,0.06)',
    scheme:        'light',
  },
  'noon': {
    bg:            '#FFFFFF',
    bgCard:        '#FBFDFF',
    bgInset:       '#EDF3FA',
    text:          '#0F172A',
    textSecondary: '#45556C',
    textTertiary:  '#8090A5',
    border:        'rgba(15,23,42,0.1)',
    accent:        '#2563EB',
    accentSoft:    'rgba(37,99,235,0.08)',
    accentBorder:  'rgba(37,99,235,0.28)',
    success:       '#059669',
    successSoft:   'rgba(5,150,105,0.08)',
    warning:       '#d97706',
    warningSoft:   'rgba(217,119,6,0.1)',
    danger:        '#dc2626',
    dangerSoft:    'rgba(220,38,38,0.08)',
    shadow:        '0 4px 14px rgba(15,23,42,0.05)',
    scheme:        'light',
  },
  'afternoon': {
    bg:            '#F5F6F8',
    bgCard:        '#FDFEFE',
    bgInset:       '#E9ECF1',
    text:          '#1B2330',
    textSecondary: '#4C5968',
    textTertiary:  '#8A94A2',
    border:        'rgba(27,35,48,0.1)',
    accent:        '#0891B2',
    accentSoft:    'rgba(8,145,178,0.09)',
    accentBorder:  'rgba(8,145,178,0.3)',
    success:       '#059669',
    successSoft:   'rgba(5,150,105,0.1)',
    warning:       '#d97706',
    warningSoft:   'rgba(217,119,6,0.1)',
    danger:        '#dc2626',
    dangerSoft:    'rgba(220,38,38,0.08)',
    shadow:        '0 4px 14px rgba(27,35,48,0.06)',
    scheme:        'light',
  },
  'golden': {
    bg:            '#FFF0E0',
    bgCard:        '#FFF9ED',
    bgInset:       '#FBE3C5',
    text:          '#3D1F0B',
    textSecondary: '#7A4824',
    textTertiary:  '#B07A48',
    border:        'rgba(61,31,11,0.12)',
    accent:        '#D97706',
    accentSoft:    'rgba(217,119,6,0.13)',
    accentBorder:  'rgba(217,119,6,0.35)',
    success:       '#15803d',
    successSoft:   'rgba(21,128,61,0.1)',
    warning:       '#b45309',
    warningSoft:   'rgba(180,83,9,0.12)',
    danger:        '#b91c1c',
    dangerSoft:    'rgba(185,28,28,0.1)',
    shadow:        '0 6px 18px rgba(132,72,28,0.14)',
    scheme:        'light',
  },
  'dusk': {
    bg:            '#2A1F3A',
    bgCard:        '#352A47',
    bgInset:       '#3F3256',
    text:          '#EFE6F5',
    textSecondary: '#BAABC9',
    textTertiary:  '#8075A0',
    border:        'rgba(239,230,245,0.1)',
    accent:        '#E879A5',
    accentSoft:    'rgba(232,121,165,0.14)',
    accentBorder:  'rgba(232,121,165,0.32)',
    success:       '#4ade80',
    successSoft:   'rgba(74,222,128,0.12)',
    warning:       '#fbbf24',
    warningSoft:   'rgba(251,191,36,0.12)',
    danger:        '#f87171',
    dangerSoft:    'rgba(248,113,113,0.12)',
    shadow:        '0 6px 18px rgba(0,0,0,0.45)',
    scheme:        'dark',
  },
  'night': {
    bg:            '#0E0E13',
    bgCard:        '#17171E',
    bgInset:       '#1E1E26',
    text:          '#F2F2F5',
    textSecondary: '#B8B8C2',
    textTertiary:  '#7A7A86',
    border:        'rgba(242,242,245,0.08)',
    accent:        '#7C3AED',
    accentSoft:    'rgba(124,58,237,0.12)',
    accentBorder:  'rgba(124,58,237,0.3)',
    success:       '#10b981',
    successSoft:   'rgba(16,185,129,0.12)',
    warning:       '#eab308',
    warningSoft:   'rgba(234,179,8,0.12)',
    danger:        '#ef4444',
    dangerSoft:    'rgba(239,68,68,0.12)',
    shadow:        '0 6px 18px rgba(0,0,0,0.5)',
    scheme:        'dark',
  },
};

// Convert a palette object to `--color-<key>: value;` CSS declarations so we
// can inject them on the document root.
export function paletteToCssVars(palette) {
  return Object.entries(palette)
    .filter(([k]) => k !== 'scheme')
    .map(([k, v]) => `--color-${kebab(k)}: ${v};`)
    .join(' ');
}

function kebab(s) {
  return s.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase());
}
